import { useEffect, useRef, useState } from 'react';

const SecondaryDropdownContainer = ({ children, isOpen, toggleDropdown, additionalclasses, isNested, style }) => {
    const dropdownRef = useRef(null);
    const parentScrollRef = useRef(null);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const [isVisible, setIsVisible] = useState(false);
    const resizeObserverRef = useRef(null);

    const handleClickOutside = (event) => {
        if (!dropdownRef.current) return;
        
        // Check if the click is inside the dropdown
        if (dropdownRef.current.contains(event.target)) {
            // Click is inside dropdown, do nothing
            return;
        }
        
        // If we reach here, click is outside of dropdown
        setTimeout(() => {
            toggleDropdown();
        }, 0);
    };

    const findScrollableParent = (element) => {
        while (element) {
            const { overflowX, overflowY } = window.getComputedStyle(element);
            if (['scroll', 'auto'].includes(overflowX) || ['scroll', 'auto'].includes(overflowY)) {
                return element;
            }
            element = element.parentElement;
        }
        return window;
    };

    const handleOverflow = () => {
        if (!dropdownRef.current) return;

        const dropdown = dropdownRef.current;

        // Reset any previous transform so we measure the natural position
        dropdown.style.transform = '';

        const dropdownRect = dropdown.getBoundingClientRect();
        // Always use the viewport as the boundary so nested dropdowns work correctly
        // Account for 55px bottom UI (e.g. bottom nav bar)
        const bottomPadding = 55;
        const parentRect = { top: 0, bottom: window.innerHeight - bottomPadding, left: 0, right: window.innerWidth };

        // Calculate adjustments needed as translation deltas
        let adjustTop = 0;
        let adjustLeft = 0;
        const padding = 8;

        // Check vertical overflow against container bounds
        if (dropdownRect.bottom > parentRect.bottom) {
            adjustTop = parentRect.bottom - dropdownRect.bottom - padding;
        }
        if (dropdownRect.top < parentRect.top) {
            adjustTop = parentRect.top - dropdownRect.top + padding;
        }

        // Check horizontal overflow against container bounds
        if (dropdownRect.right > parentRect.right) {
            adjustLeft = parentRect.right - dropdownRect.right - padding;
        }
        if (dropdownRect.left < parentRect.left) {
            adjustLeft = parentRect.left - dropdownRect.left + padding;
        }

        // Apply adjustments via transform so class-based positioning is unaffected
        if (adjustTop !== 0 || adjustLeft !== 0) {
            dropdown.style.transform = `translate(${adjustLeft}px, ${adjustTop}px)`;
        }
    };

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (!isOpen) return;

            const options = dropdownRef.current?.querySelectorAll('.dropdown-option');
            if (!options || options.length === 0) return;

            switch (event.key) {
                case 'ArrowDown':
                    event.preventDefault();
                    setFocusedIndex((prevIndex) => (prevIndex + 1) % options.length);
                    break;
                case 'ArrowUp':
                    event.preventDefault();
                    setFocusedIndex((prevIndex) => (prevIndex - 1 + options.length) % options.length);
                    break;
                case 'Enter':
                    event.preventDefault();
                    if (focusedIndex >= 0 && focusedIndex < options.length) {
                        options[focusedIndex].click();
                    }
                    break;
                default:
                    break;
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, focusedIndex]);

    useEffect(() => {
        if (focusedIndex >= 0 && dropdownRef.current) {
            const options = dropdownRef.current.querySelectorAll('.dropdown-option');
            if (options[focusedIndex]) {
                options[focusedIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        }
    }, [focusedIndex]);

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('mouseup', handleClickOutside);
            setIsVisible(false);
            // Use a small delay to ensure the dropdown is rendered before calculating position
            setTimeout(() => {
                requestAnimationFrame(() => {
                    handleOverflow();
                    setIsVisible(true);

                    // Watch for size changes while open and recalculate position
                    if (dropdownRef.current) {
                        resizeObserverRef.current = new ResizeObserver(() => {
                            handleOverflow();
                        });
                        resizeObserverRef.current.observe(dropdownRef.current);
                    }
                });
            }, 0);
        } else {
            document.removeEventListener('mouseup', handleClickOutside);
            setIsVisible(false);
            if (resizeObserverRef.current) {
                resizeObserverRef.current.disconnect();
                resizeObserverRef.current = null;
            }
        }

        return () => {
            document.removeEventListener('mouseup', handleClickOutside);
            if (resizeObserverRef.current) {
                resizeObserverRef.current.disconnect();
                resizeObserverRef.current = null;
            }
            if (dropdownRef.current) {
                dropdownRef.current.style.transform = '';
            }
        };
    }, [isOpen]);

    useEffect(() => {
        if (dropdownRef.current) {
            const options = dropdownRef.current.querySelectorAll('.dropdown-option');
            options.forEach((option, index) => {
                if (index === focusedIndex) {
                    option.classList.add('focused-option');
                } else {
                    option.classList.remove('focused-option');
                }
            });
        }
    }, [focusedIndex]);

    return (
        <>
            {isOpen &&
                <div ref={dropdownRef} style={style} className={`absolute top-8 left-0 z-50 min-w-[200px] lg:min-w-[242px] bg-white-50 dark:bg-grey-600 rounded-[8px] custom-shadow grid content-start p-[4px] ignore-click overflow-y-auto scrollbar-none dropdown-container gap-[4px] ${additionalclasses} ${isVisible ? '' : 'opacity-0 pointer-events-none'}`}>
                    {children}
                </div>
            }
        </>
    );
};

export default SecondaryDropdownContainer;